import { z } from 'zod'
import { queryOptions } from '@tanstack/react-query'
import { baseUrl, getJson, postJson } from '@/api/http'

export const FeedbackStaySchema = z.object({
  firstName: z.string(),
  locationName: z.string(),
  start: z.string(),
  end: z.string(),
  locationRating: z.number().nullable(),
  locationComment: z.string().nullable(),
  platformRating: z.number().nullable(),
  platformComment: z.string().nullable(),
  submitted: z.boolean(),
})

export type FeedbackStay = z.infer<typeof FeedbackStaySchema>

export interface FeedbackValues {
  locationRating: number | null
  locationComment: string
  platformRating: number | null
  platformComment: string
}

const responseSchema = z.object({ status: z.string() })

/** The stay a feedback link from the thank-you e-mail belongs to. */
export function fetchFeedbackStay(orderNumber: string, token: string, signal?: AbortSignal): Promise<FeedbackStay> {
  return getJson(`${baseUrl}/order/${orderNumber}/feedback/${token}`, FeedbackStaySchema, { signal })
}

/**
 * Saves the whole feedback at once — the form posts on every change so we keep
 * the answers even when the guest never reaches the confirmation button.
 * `submitted` marks the explicit confirmation.
 */
export function saveFeedback(
  orderNumber: string,
  token: string,
  values: FeedbackValues,
  submitted: boolean,
): Promise<unknown> {
  return postJson(
    `${baseUrl}/order/${orderNumber}/feedback/${token}`,
    {
      locationRating: values.locationRating,
      locationComment: values.locationComment.trim() || null,
      platformRating: values.platformRating,
      platformComment: values.platformComment.trim() || null,
      submitted,
    },
    responseSchema,
  )
}

export const feedbackStayQueryOptions = (orderNumber: string, token: string) =>
  queryOptions<FeedbackStay>({
    queryKey: ['feedback', orderNumber, token],
    queryFn: ({ signal }) => fetchFeedbackStay(orderNumber, token, signal),
  })
